import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { COMPANY } from "../data/site";
import { Mail, Phone, MapPin } from "lucide-react";
import logoFull from "../assets/logo-full.png";
import { useLocalePath } from "../lib/useLocalePath";

const LINKS = [
    ["/about", "about"],
    ["/products", "products"],
    ["/process", "process"],
    ["/export", "export"],
    ["/gallery", "gallery"],
    ["/contact", "contact"],
];

export const Footer = () => {
    const { t } = useTranslation();
    const lp = useLocalePath();

    return (
        <footer
            data-testid="site-footer"
            className="bg-[#0D0D0D] border-t border-[#2C2C2C]"
        >
            <div className="px-6 md:px-12 lg:px-20 py-20 grid grid-cols-1 md:grid-cols-12 gap-12">
                <div className="md:col-span-5">
                    <img
                        src={logoFull}
                        alt="Jagruthi Stones LLP"
                        className="w-52 max-w-full h-auto"
                    />
                    <p className="mt-5 font-heading italic text-lg md:text-xl text-[#C5A059]">
                        {t("footer.tagline")}
                    </p>
                    <p className="mt-5 text-[#A39E93] max-w-md leading-relaxed font-light">
                        {t("footer.blurb")}
                    </p>
                </div>

                <div className="md:col-span-3">
                    <p className="text-[10px] uppercase tracking-[0.35em] text-[#A39E93]">
                        {t("footer.navigate")}
                    </p>
                    <ul className="mt-5 space-y-3 text-[#E8E3D9]">
                        {LINKS.map(([to, key]) => (
                            <li key={to}>
                                <Link
                                    to={lp(to)}
                                    data-testid={`footer-link-${key}`}
                                    className="text-sm hover:text-[#C5A059] transition-colors"
                                >
                                    {t(`nav.${key}`)}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="md:col-span-4">
                    <p className="text-[10px] uppercase tracking-[0.35em] text-[#A39E93]">
                        {t("footer.reach_out")}
                    </p>
                    <ul className="mt-5 space-y-4 text-[#E8E3D9] text-sm">
                        <li className="flex gap-3">
                            <MapPin
                                size={16}
                                className="text-[#C5A059] mt-1 shrink-0"
                            />
                            <span className="font-light leading-relaxed">
                                {COMPANY.address.line1}
                                <br />
                                {COMPANY.address.line2}
                                <br />
                                {COMPANY.address.district},{" "}
                                {COMPANY.address.state}{" "}
                                {COMPANY.address.postal},{" "}
                                {COMPANY.address.country}
                            </span>
                        </li>
                        <li className="flex gap-3">
                            <Phone
                                size={16}
                                className="text-[#C5A059] shrink-0"
                            />
                            <a
                                href={`tel:${COMPANY.phone.replace(/\s/g, "")}`}
                                data-testid="footer-phone"
                                className="hover:text-[#C5A059] transition-colors"
                            >
                                {COMPANY.phone}
                            </a>
                        </li>
                        <li className="flex gap-3">
                            <Mail size={16} className="text-[#C5A059] shrink-0" />
                            <a
                                href={`mailto:${COMPANY.email}`}
                                data-testid="footer-email"
                                className="hover:text-[#C5A059] transition-colors"
                            >
                                {COMPANY.email}
                            </a>
                        </li>
                    </ul>
                </div>
            </div>

            <div className="border-t border-[#2C2C2C]">
                <div className="px-6 md:px-12 lg:px-20 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
                    <p className="text-xs text-[#A39E93]">
                        © {new Date().getFullYear()} {COMPANY.legalName}.{" "}
                        {t("footer.rights")}
                    </p>
                    <p className="text-[10px] uppercase tracking-[0.3em] text-[#A39E93]">
                        {t("footer.meta_line")}
                    </p>
                </div>
            </div>
        </footer>
    );
};
